import type { OrgState } from './org-selectors';
import { SUCCESS, STARTING, FAILURE } from '../action/util/action-name-creators';

import {
  Action,
  updateStatusById,
  updateResourceByParam,
  setStatusDirtyById,
  updateResourceByObjectKey,
} from './utils/state-manager';

const InitialState: OrgState = {
  orgsById: {},
  orgsStateById: {},
  orgsStateByAccountId: {},
  orgProcessPerformanceById: {},
  orgProcessPerformanceByIdStatus: {},
  orgMergeEmployeeMetaPostByOrgId: {},
  orgMergeEmployeeMetaPostStateByOrgId: {},
};

export default (state = InitialState, action: Action): OrgState => {
  switch (action.type) {
    case STARTING('LOAD_ORG'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'orgs', 'id', orgId, true);
    }
    case SUCCESS('LOAD_ORG'): {
      const {
        args: { orgId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'orgs', 'id', orgId, false, data);
    }
    case FAILURE('LOAD_ORG'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'orgs', 'id', orgId, false);
    }
    case STARTING('LOAD_ORGS'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'orgs', 'accountId', accountId, true);
    }
    case SUCCESS('LOAD_ORGS'): {
      const {
        args: { accountId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'orgs', 'accountId', accountId, false, data);
    }
    case FAILURE('LOAD_ORGS'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'orgs', 'accountId', accountId, false);
    }

    case STARTING('UPDATE_ORGANIZATION'): {
      const {
        args: { orgId },
      } = action.payload;
      return updateStatusById(state, 'orgsById', true, orgId);
    }
    case SUCCESS('UPDATE_ORGANIZATION'): {
      const {
        args: { orgId },
        data,
      } = action.payload;
      // There are some valid failure conditions here, like not having a valid location
      // We shouldn't do anything in that case. Note: there is a bit of trickery here, where
      // we are actually tracking the status of the update organization call. By returning the state
      // here, we are basically stopping future requests to the update endpoint. This is an edge case
      // and if we try and update an org that is missing a location, we'll know from other alerts
      if (data.errorMessage) {
        return state;
      }
      return updateStatusById(state, 'orgsById', false, orgId, data);
    }
    case SUCCESS('REPLACE_PHONE_NUMBERS_BY_AREA_CODE'): {
      const {
        args: { orgId },
        data,
      } = action.payload;
      const { errorMessage, org } = data;
      if (errorMessage) {
        return state;
      }
      return updateStatusById(state, 'orgsById', false, orgId, org);
    }
    case FAILURE('UPDATE_ORGANIZATION'): {
      const {
        args: { orgId },
      } = action.payload;
      return updateStatusById(state, 'orgsById', false, orgId);
    }
    case SUCCESS('ADD_ORGANIZATION'): {
      state.orgsById = {
        ...state.orgsById,
        [action.payload.data.org_id]: action.payload.data,
      };
      return state;
    }
    case SUCCESS('TOGGLE_SEND_JOBS_TO_INDEED'): {
      const orgs = { ...state.orgsById };
      orgs[action.payload.args.orgId] = {
        ...orgs[action.payload.args.orgId],
        sendJobsToIndeed: action.payload.args.sendToIndeed,
      };
      return { ...state, orgsById: orgs };
    }
    case STARTING('ORG_APPLICANT_SUMMARY'): {
      const { args } = action.payload;
      return updateStatusById(state, 'orgProcessPerformanceById', true, args.orgId);
    }
    case SUCCESS('ORG_APPLICANT_SUMMARY'): {
      const { args } = action.payload;
      return updateStatusById(
        state,
        'orgProcessPerformanceById',
        false,
        args.orgId,
        action.payload.data,
      );
    }
    case FAILURE('ORG_APPLICANT_SUMMARY'): {
      const { args } = action.payload;
      return updateStatusById(state, 'orgProcessPerformanceById', false, args.orgId);
    }
    case 'SET_ORG_APPLICANT_SUMMARY_DIRTY': {
      const { orgId } = action.payload.args;
      return setStatusDirtyById(state, 'orgProcessPerformanceById', orgId);
    }
    case SUCCESS('CREATE_QUICKBOOKS_INTEGRATION'): {
      const { args, data } = action.payload;
      const updatedOrg = { ...state.orgsById[args.orgId], QuickbooksIntegration: data };
      const newOrgsById = { ...state.orgsById, [args.orgId]: updatedOrg };
      const newState = { ...state, orgsById: newOrgsById };
      return newState;
    }
    case SUCCESS('CREATE_V_CARD'): {
      const { orgId } = action.payload.args;
      const updatedOrg = action.payload.data.org;
      const newOrgsById = { ...state.orgsById, [orgId]: updatedOrg };
      const newState = { ...state, orgsById: newOrgsById };
      return newState;
    }
    case STARTING('GET_MERGE_EMPLOYEE_META_POST'): {
      const {
        payload: {
          args: { orgId },
        },
      } = action;
      return updateResourceByObjectKey(state, 'orgMergeEmployeeMetaPostByOrgId', orgId, true);
    }
    case SUCCESS('GET_MERGE_EMPLOYEE_META_POST'): {
      const {
        payload: {
          args: { orgId },
          data,
        },
      } = action;
      return updateResourceByObjectKey(state, 'orgMergeEmployeeMetaPostByOrgId', orgId, false, {
        [orgId]: data.mergeResponse,
      });
    }
    case FAILURE('GET_MERGE_EMPLOYEE_META_POST'): {
      const {
        payload: {
          args: { orgId },
        },
      } = action;
      return updateResourceByObjectKey(state, 'orgMergeEmployeeMetaPostByOrgId', orgId, false);
    }
    default:
      return state;
  }
};
